import { API_BASE } from '../config/constants';

const RESPONSE_TTL_MS = 60_000;
const REQUEST_TIMEOUT_MS = 15_000;
const responseCache = new Map();
const pendingGets = new Map();
let refreshPromise = null;

export class ApiError extends Error {
  constructor(message, { status = 0, code = 'API_ERROR' } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function isCacheableGet(path, method) {
  return method === 'GET' && /^\/api\/(products|categories|settings|banners|reviews)(\/|\?|$)/.test(path);
}

async function parseResponse(response) {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError('The server returned an invalid response.', {
      status: response.status,
      code: 'INVALID_RESPONSE',
    });
  }
}

async function doRefreshToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: controller.signal,
        });
        const data = await parseResponse(response);
        if (!response.ok || !data.token) {
          throw new ApiError(data.message || 'Session refresh failed.', {
            status: response.status,
            code: 'REFRESH_FAILED',
          });
        }
        localStorage.setItem('token', data.token);
        return data.token;
      } catch (error) {
        localStorage.removeItem('token');
        throw error;
      } finally {
        window.clearTimeout(timeout);
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

async function performRequest(path, options) {
  const {
    forceRefresh,
    timeoutMs = REQUEST_TIMEOUT_MS,
    ...fetchOptions
  } = options;
  const method = String(fetchOptions.method || 'GET').toUpperCase();
  const isAuthRoute = /\/auth\/(login|register|refresh|forgot-password|reset-password)/.test(path);
  const token = localStorage.getItem('token');
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  const externalSignal = fetchOptions.signal;
  const abortFromCaller = () => controller.abort();
  externalSignal?.addEventListener('abort', abortFromCaller, { once: true });
  delete fetchOptions.signal;

  const requestOptions = {
    ...fetchOptions,
    method,
    credentials: 'include',
    signal: controller.signal,
    headers: {
      ...(fetchOptions.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...fetchOptions.headers,
    },
  };

  try {
    let response;
    try {
      response = await fetch(url, requestOptions);
    } catch (error) {
      if (controller.signal.aborted) {
        throw new ApiError(
          externalSignal?.aborted ? 'Request cancelled.' : 'The request timed out. Please try again.',
          { code: externalSignal?.aborted ? 'REQUEST_CANCELLED' : 'REQUEST_TIMEOUT' },
        );
      }
      throw new ApiError('Unable to connect to the store right now.', { code: 'NETWORK_ERROR' });
    }

    if (response.status === 401 && !isAuthRoute && token) {
      const newToken = await doRefreshToken();
      requestOptions.headers = { ...requestOptions.headers, Authorization: `Bearer ${newToken}` };
      response = await fetch(url, requestOptions);
    }

    const payload = await parseResponse(response);
    if (!response.ok) {
      throw new ApiError(payload?.message || 'Something went wrong. Please try again.', {
        status: response.status,
        code: payload?.code || 'HTTP_ERROR',
      });
    }
    return payload;
  } finally {
    window.clearTimeout(timeout);
    externalSignal?.removeEventListener('abort', abortFromCaller);
  }
}

export async function request(path, options = {}) {
  const method = String(options.method || 'GET').toUpperCase();
  const cacheable = isCacheableGet(path, method);
  const cacheKey = `${method}:${path}`;
  const cached = responseCache.get(cacheKey);
  if (cacheable && !options.forceRefresh && cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }
  if (cacheable && !options.forceRefresh && pendingGets.has(cacheKey)) {
    return pendingGets.get(cacheKey);
  }

  const pending = performRequest(path, options)
    .then((value) => {
      if (cacheable) responseCache.set(cacheKey, { value, expiresAt: Date.now() + RESPONSE_TTL_MS });
      return value;
    })
    .finally(() => {
      if (pendingGets.get(cacheKey) === pending) pendingGets.delete(cacheKey);
    });
  if (cacheable) pendingGets.set(cacheKey, pending);
  return pending;
}
